import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';

// Approvals hub — reads the cross-company aggregator (GET /approvals) and routes each
// Approve/Reject to that stream's OWN existing endpoint (the act-map). It adds NO approve
// logic: the real endpoint enforces every guard (a 403 here means the gate held).
const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };

// type -> {approve, reject}. Each calls the stream's existing gated endpoint.
const ACT = {
  purchase_order: {   // trims POs are ordinary purchase_order rows now (stem grouping removed)
    approve: (id) => api.patch(`/procurement/pos/${id}/approve`),
    reject: (id, reason) => api.patch(`/procurement/pos/${id}/reject`, { rejection_reason: reason }),
  },
  payment: {
    approve: (id) => api.patch(`/requests/${id}/decision`, { decision: 'approved' }),
    reject: (id, reason) => api.patch(`/requests/${id}/decision`, { decision: 'rejected', remarks: reason }),
  },
  indent_general: {
    approve: (id) => api.patch(`/requests/${id}/decision`, { decision: 'approved' }),
    reject: (id, reason) => api.patch(`/requests/${id}/decision`, { decision: 'rejected', remarks: reason }),
  },
  deroi_vendor: {   // routes to the existing gated Deroi-vendor approval endpoints
    approve: (id) => api.patch(`/procurement/deroi-vendors/${id}/approve`),
    reject: (id, reason) => api.patch(`/procurement/deroi-vendors/${id}/reject`, { rejection_reason: reason }),
  },
  // Job-work decisions (design §F). Both endpoints already existed and had NO
  // frontend caller at all: these two rows rendered a "Review →" link into
  // /job-work, a screen with no control, so a pending over-receipt or over-gate
  // delivery date could not be decided anywhere in the UI. JWO-RSR-1745-001 has
  // been stuck at date_approval_status='pending' (12d negotiated vs a 4d
  // benchmark) for exactly this reason.
  //
  // The hub adds NO authority — both endpoints keep authorize('admin','management')
  // and their own preconditions (excess_qty > 0, status still 'pending'), so a
  // stale row returns a 409 that the hub surfaces rather than silently succeeding.
  jobwork_date: {
    approve: (id) => api.patch(`/jobwork/${id}/date-decision`, { decision: 'approved' }),
    reject: (id, reason) => api.patch(`/jobwork/${id}/date-decision`, { decision: 'rejected', reason }),
  },
  // AN OVER-RECEIPT IS APPROVED WITH A REASON, by a named person in scope (mig 421, 28 Oct 2026): the endpoint now
  // refuses a bare approve, so the confirm cell collects the reason the same way the held receipt's does.
  jobwork_excess: {
    approve: (id, extra) => api.patch(`/jobwork/${id}/excess-decision`, { decision: 'approved', reason: extra?.reason }),
    reject: (id, reason) => api.patch(`/jobwork/${id}/excess-decision`, { decision: 'rejected', reason }),
  },
  // A HELD RECEIPT IS NOT A BARE APPROVE (step 7). It is decided with a REASON — reweighed,
  // moisture, or the knitter's own yarn — and a RULED FIGURE that the lot and the cost book at.
  // approve() therefore takes an extra argument the other streams do not have; the confirm cell
  // below collects it, and the endpoint validates the list and bars the person who recorded it.
  jobwork_hold: {
    approve: (id, extra) => api.patch(`/jobwork/challans/${id}/excess-resolution`, extra),
    reject: (id, reason) => api.patch(`/jobwork/challans/${id}/excess-resolution`, { resolution: 'rejected', reason }),
  },
};

// WHERE "Review →" LANDS. detail_link is the backend's default; several streams emit a bare
// module path (/job-work, /procurement?tab=deroi_ops) that drops the approver on a landing
// screen rather than the record. The receiving screens already parse ?tab= — JobWork.js accepts
// rsr|deroi|grn|invoices|material — so the link just has to name the right one.
//   · jobwork_invoice → the Invoices tab, where the invoice being approved is listed.
//   · jobwork_excess  → the GRN register: an over-receipt is a RECEIPT fact, and the backend
//                       notes the decision needs the GRN context.
//   · jobwork_date    → the JWO register for that company (the negotiated date lives on the JWO).
//   · deroi_vendor    → /deroi. It pointed at /procurement?tab=deroi_ops, but deroi_ops is NOT
//                       in the /procurement tab set (it only exists under the /deroi route, which
//                       renders <Procurement deroiMode />), so the tab test failed and the param
//                       was silently ignored.
// Anything already carrying a usable target (e.g. /procurement?tab=pos&open=<po_no>) passes
// straight through untouched.
const LINK_OVERRIDE = {
  jobwork_invoice: () => '/job-work?tab=invoices',
  jobwork_excess: () => '/job-work?tab=grn',
  jobwork_hold: () => '/job-work?tab=challans',
  jobwork_date: (r) => `/job-work?tab=${r.company === 'Deroi' ? 'deroi' : 'rsr'}`,
  deroi_vendor: () => '/deroi',
};
const linkFor = (r) => (LINK_OVERRIDE[r.type] ? LINK_OVERRIDE[r.type](r) : r.detail_link);

const TYPE_LABEL = {
  purchase_order: 'Order PO', payment: 'Payment', indent_general: 'Indent',
  vendor_application: 'Vendor', knp: 'KNP', gpo_component: 'Declaration', risk_analysis: 'Risk/GPO',
  deroi_vendor: 'Deroi Vendor', jobwork_invoice: 'Job-work Inv', budget: 'Budget',
  jobwork_excess: 'Over-receipt', jobwork_hold: 'Held receipt', jobwork_date: 'Delivery Date',
};

// Price-context strip in the hub — approvers commit money inline here without opening the
// detail, so the same heads-up from the PO screen (commit 1bed08d) must appear. Lazy-loads
// GET /procurement/price-context per line AFTER the list paints (shows "…" then fills), so N
// context calls never block the hub. Single-line PO → full strip; multi-line → worst chip +
// "detail →" (the per-line strips live on the PO screen). v1 fuzzy match (see backend note).
const rupee = (n) => '₹' + Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const CHIP = [{ bg: '#EAF7EE', fg: '#166534' }, { bg: '#FAEEDA', fg: '#854F0B' }, { bg: '#FCEBEB', fg: '#A32D2D' }];
// severity vs last price: 0 green ≤5%, 1 amber 5-15%, 2 red >15%; -1 = history but no comparable rate
const chipInfo = (pc, unit_price) => {
  const lp = pc.last_price?.price, cur = num(unit_price);
  if (lp == null || !(cur > 0) || !(num(lp) > 0)) return { level: -1, pc };
  const dev = Math.abs(cur - num(lp)) / num(lp) * 100;
  return { level: dev <= 5 ? 0 : dev <= 15 ? 1 : 2, dev, up: cur >= num(lp), pc };
};
const chipEl = (info) => {
  if (!info || info.level < 0) return null;
  const c = CHIP[info.level];
  return <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 8, background: c.bg, color: c.fg, fontWeight: 700, fontSize: 10 }}>{info.up ? '▲' : '▼'}{info.dev.toFixed(0)}%</span>;
};
// This PO's own offered terms for a line: "190 rolls @ ₹80" (qty + unit verbatim + rate).
const offerText = (l) => `${num(l.qty).toLocaleString('en-IN')} ${l.unit || ''} @ ${rupee(l.unit_price)}`.replace(/ +/g, ' ').trim();
// This-rate vs COST-ESTIMATE (Rajan's ruling): >25% over → red, >10% over → amber; at/under
// estimate → no flag. Only fires when the offer OVERshoots the estimate.
const estChip = (estimate, unit_price) => {
  const est = num(estimate), cur = num(unit_price);
  if (!(est > 0) || !(cur > 0)) return null;
  const dev = (cur - est) / est * 100;   // signed
  if (dev <= 10) return null;
  return { level: dev > 25 ? 2 : 1, dev };
};
const estChipEl = (info) => info ? <span style={{ marginLeft: 4, padding: '1px 6px', borderRadius: 8, background: CHIP[info.level].bg, color: CHIP[info.level].fg, fontWeight: 700, fontSize: 10 }}>▲{info.dev.toFixed(0)}% vs est</span> : null;
function PriceContextCell({ lines, detailLink, gpoNo }) {
  const [res, setRes] = React.useState(undefined);   // undefined = context loading, array = loaded
  React.useEffect(() => {
    let live = true;
    if (!lines || !lines.length) { setRes([]); return; }
    Promise.all(lines.map(l =>
      api.get('/procurement/price-context', { params: { item_desc: l.item_desc, supplier_id: l.supplier_id || undefined, gpo_no: gpoNo || undefined } })
        .then(r => ({ l, pc: r.data.data })).catch(() => ({ l, pc: null }))
    )).then(out => { if (live) setRes(out); });
    return () => { live = false; };
  }, [JSON.stringify(lines), gpoNo]);   // eslint-disable-line react-hooks/exhaustive-deps
  const offerStyle = { marginTop: 4, fontSize: 11, color: '#555', fontWeight: 600 };
  const ctxStyle = { fontSize: 11, color: '#777' };

  // SINGLE-LINE (the common hub case): the offer line renders instantly (from lines), the
  // market strip fills in beneath after the lazy fetch — the row reads offer → market.
  if (lines.length === 1) {
    const l = lines[0];
    let ctx;
    if (res === undefined) ctx = <span style={{ color: '#aaa' }}>price history …</span>;
    else {
      const pc = res[0]?.pc || {};
      // Est shows ALWAYS (independent of PO market history): the cost-estimate rate + flag,
      // or "Est —" honestly when the GPO has no estimate / no matching line.
      const estEl = pc.estimate_rate != null
        ? <><span style={{ fontWeight: 600 }}>Est {rupee(pc.estimate_rate)}</span>{estChipEl(estChip(pc.estimate_rate, l.unit_price))}</>
        : <span style={{ color: '#aaa' }}>Est —</span>;
      const hasHist = pc.last_price || pc.median != null;
      ctx = (<>
        {estEl}
        {pc.last_price && <> · Last {rupee(pc.last_price.price)} ({pc.last_price.vendor_name})</>}
        {pc.median != null && <> · Med {rupee(pc.median)} (n={pc.match_count})</>}
        {hasHist && chipEl(chipInfo(pc, l.unit_price))}
        {!hasHist && <span style={{ color: '#B08900' }}> · no price history</span>}
      </>);
    }
    return (<>
      <div style={offerStyle}>{offerText(l)}</div>
      <div style={ctxStyle}>{ctx}</div>
    </>);
  }
  // MULTI-LINE: compact — the worst (most-severe) line's offer + chip + detail link.
  if (res === undefined) return (<>
    <div style={offerStyle}>{lines.length} lines</div>
    <div style={{ ...ctxStyle, color: '#aaa' }}>price history …</div>
  </>);
  const hist = res.map(x => ({ ...x, info: (x.pc && x.pc.match_count) ? chipInfo(x.pc, x.l.unit_price) : null })).filter(x => x.info);
  const worst = hist.reduce((a, b) => (b.info.level > (a ? a.info.level : -2) ? b : a), null);
  // Worst estimate overage across the lines (This vs Est) — the money heads-up for a stem.
  const estHist = res.map(x => ({ l: x.l, est: x.pc?.estimate_rate, info: estChip(x.pc?.estimate_rate, x.l.unit_price) })).filter(x => x.info);
  const estWorst = estHist.reduce((a, b) => (b.info.level > (a ? a.info.level : -2) ? b : a), null);
  return (
    <div style={{ marginTop: 4, ...ctxStyle }}>
      <span style={{ color: '#555', fontWeight: 600 }}>{lines.length} lines</span>
      {estWorst && <> · over est: <strong>{estWorst.l.item_desc}</strong> {rupee(estWorst.l.unit_price)} vs Est {rupee(estWorst.est)}{estChipEl(estWorst.info)}</>}
      {worst && worst.info.level >= 0 && <> · worst <strong>{worst.l.item_desc}</strong> {offerText(worst.l)}{chipEl(worst.info)}</>}
      {!hist.length && !estWorst && ' · no price history'}
      {detailLink && <> · <a href={detailLink} style={{ color: '#3B3A8F', fontWeight: 600, textDecoration: 'none' }}>detail →</a></>}
    </div>
  );
}

// Indent line items — the general-store equivalent of a PO's per-line detail. Shows the
// line count + every "item — qty unit @ ₹rate = ₹value" so an approver sees WHAT the indent
// contains before approving. Reads the lines returned on the hub row; blanks (—) where a
// field genuinely doesn't exist. "detail →" opens the full request screen.
function IndentLinesCell({ lines, detailLink }) {
  const rup = (v) => (v != null ? '₹' + Number(v).toLocaleString('en-IN') : '—');
  return (
    <div style={{ marginTop: 4, fontSize: 11, color: '#555' }}>
      <div style={{ fontWeight: 600, color: '#3B3A8F' }}>
        {lines.length} line{lines.length > 1 ? 's' : ''}
        {detailLink && <> · <a href={detailLink} style={{ color: '#3B3A8F', fontWeight: 600, textDecoration: 'none' }}>detail →</a></>}
      </div>
      <div style={{ maxHeight: 148, overflowY: 'auto', marginTop: 2, borderLeft: '2px solid #EEEDF8', paddingLeft: 6 }}>
        {lines.map((l, i) => {
          const h = l.hist;
          // Consumables have no estimate — show prior-purchase history (Last · Avg · Low) for the
          // SAME stock item. "no history" honestly when this item was never ordered before.
          const histEl = !h ? null
            : (h.n > 0
              ? <span style={{ color: '#777' }}> · Last {rup(h.last)} · Avg {rup(h.avg)} · Low {rup(h.low)} (n={h.n})</span>
              : <span style={{ color: '#B08900' }}> · no history</span>);
          return (
            <div key={i} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              title={`${l.item_desc || ''} — ${l.qty ?? '—'} ${l.unit || ''} @ ${rup(l.rate)} = ${rup(l.amount)}`}>
              <span style={{ color: '#333' }}>{l.item_desc || '—'}</span>
              {' — '}{l.qty != null ? Number(l.qty).toLocaleString('en-IN') : '—'} {l.unit || ''} @ {rup(l.rate)}
              {l.amount != null && <span style={{ color: '#888' }}> = {rup(l.amount)}</span>}
              {histEl}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── OVER-RECEIPT DECISIONS THAT NEED A NAMED COUNTERSIGN (mig 421, 28 Oct 2026) ──────────────────────────────
// Decided before the rule, by a shared login or one scoped to the other company. They are not rewritten: a named
// person in scope stands behind each, once, with a reason, beside the original. Module scope (focus gate): it holds
// its own state. Shown only when the list is non-empty; the list route is admin/management, so others see nothing.
function ExcessCountersign() {
  const [rows, setRows] = React.useState([]);
  const [open, setOpen] = React.useState('');
  const [why, setWhy] = React.useState('');
  const [msg, setMsg] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const load = React.useCallback(() => {
    api.get('/jobwork/excess-countersign').then(r => setRows(r.data.data || [])).catch(() => setRows([]));
  }, []);
  React.useEffect(() => { load(); }, [load]);
  const sign = async (row) => {
    setBusy(true); setMsg('');
    try {
      await api.post(`/jobwork/${row.jwo_id}/excess-countersign`, { reason: why.trim() });
      setMsg(`✅ ${row.jwo_no} countersigned. The original decision is unchanged.`); setOpen(''); setWhy(''); load();
    } catch (e) { setMsg(`${row.jwo_no}: ${e.response?.data?.error || 'Countersign failed'}`); }
    finally { setBusy(false); }
  };
  if (!rows.length && !msg) return null;
  return (
    <div style={{ border: '1px solid #F0D9A8', background: '#FFFBF0', borderRadius: 8, padding: '10px 12px', marginBottom: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#7A5200', marginBottom: 6 }}>
        Over-receipt decisions that need a named countersign ({rows.length})
      </div>
      {msg && <div style={{ fontSize: 12, marginBottom: 6, color: msg.startsWith('✅') ? '#166534' : '#A32D2D' }}>{msg}</div>}
      {rows.map(r => (
        <div key={r.jwo_id} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12, padding: '4px 0', borderTop: '1px solid #F5E6C4' }}>
          <span style={{ fontFamily: 'monospace', color: '#3B3A8F' }}>{r.jwo_no}</span>
          <span>{Number(r.excess_qty)} kg {r.excess_status} · ₹{Number(r.value).toLocaleString('en-IN')}</span>
          <span style={{ color: '#7A5200' }}>by {r.approved_by_name || 'nobody named'} — {r.why_needed}</span>
          {open === r.jwo_id ? (<>
            <input autoFocus value={why} onChange={e => setWhy(e.target.value)} placeholder="Why you stand behind it (required)"
              style={{ padding: '4px 8px', border: '1px solid #E0B4B4', borderRadius: 5, fontSize: 12, width: 240 }} />
            <button disabled={busy} onClick={() => sign(r)} style={{ padding: '4px 10px', border: 'none', borderRadius: 5, background: '#166534', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: why.trim().length < 5 ? 0.45 : 1 }}>{busy ? '…' : 'Countersign'}</button>
            <button onClick={() => { setOpen(''); setWhy(''); }} style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: 5, background: 'transparent', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
          </>) : (
            <button onClick={() => { setOpen(r.jwo_id); setWhy(''); setMsg(''); }} style={{ padding: '4px 10px', border: '1px solid #D9B860', borderRadius: 5, background: '#fff', color: '#7A5200', fontSize: 12, cursor: 'pointer' }}>Countersign…</button>
          )}
        </div>
      ))}
    </div>
  );
}

export default function ApprovalsHub() {
  const location = useLocation();
  const navigate = useNavigate();

  // ── THE URL IS THE VIEW STATE ────────────────────────────────────────────────────────
  // Tab, company, type and search all live in the query string, so a filtered hub is a
  // LINK: "the four things waiting on you" can be pasted into a message instead of
  // described. It also survives a refresh, which a useState filter does not.
  //
  // Params are merged into whatever is already there — Procurement.js drives this screen
  // with ?tab=approvals and reads it back on mount (Procurement.js:1116), so blowing the
  // search string away would drop the reader back on the default tab. replace:true keeps
  // typing in the search box out of the browser's back history.
  const sp = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const status = ['pending', 'approved', 'rejected'].includes(sp.get('status')) ? sp.get('status') : 'pending';
  const company = sp.get('co') || '';
  const types = React.useMemo(() => (sp.get('ty') || '').split(',').filter(Boolean), [sp]);
  const q = sp.get('q') || '';
  const filtered = !!(company || types.length || q.trim());

  const setParams = React.useCallback((patch) => {
    const next = new URLSearchParams(location.search);
    for (const [k, v] of Object.entries(patch)) {
      const empty = v == null || v === '' || (Array.isArray(v) && !v.length);
      if (empty) next.delete(k); else next.set(k, Array.isArray(v) ? v.join(',') : v);
    }
    navigate({ pathname: location.pathname, search: next.toString() }, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const [rows, setRows] = React.useState([]);
  const [busy, setBusy] = React.useState('');
  const [err, setErr] = React.useState('');
  const [notice, setNotice] = React.useState('');
  // The row currently asking for confirmation: { key, decision }. One at a time — opening a
  // second confirmation closes the first, so there is never more than one armed action.
  const [confirming, setConfirming] = React.useState(null);
  const [reason, setReason] = React.useState('');
  const load = React.useCallback(() => {
    setErr('');
    api.get(`/approvals?status=${status}`).then(r => setRows(r.data.data || [])).catch(e => setErr(e.response?.data?.error || 'Failed to load'));
  }, [status]);
  React.useEffect(() => { load(); }, [load]);

  // The search box is typed into locally and pushed to the URL on a pause. Writing a
  // navigate() per keystroke would re-render the table on every character.
  const [qInput, setQInput] = React.useState(q);
  React.useEffect(() => { setQInput(q); }, [q]);   // back/forward, or a link opened fresh
  React.useEffect(() => {
    if (qInput === q) return;
    const t = setTimeout(() => setParams({ q: qInput }), 250);
    return () => clearTimeout(t);
  }, [qInput]);   // eslint-disable-line react-hooks/exhaustive-deps

  // ── FILTERING — client-side, over a payload already fully in memory ──────────────────
  // The endpoint has no LIMIT and no pagination: all rows for the tab arrive in one
  // response, so this is an array filter over ~60 items, not a query. IF THIS QUEUE EVER
  // PASSES ~500 ROWS, move company/type/q to the endpoint as query params — at that size
  // the payload, not the filter, becomes the cost.
  const norm = (s) => (s == null ? '' : String(s)).toLowerCase();
  const passCompany = React.useCallback((r) => !company || r.company === company, [company]);
  const passType = React.useCallback((r) => !types.length || types.includes(r.type), [types]);
  // Searched fields are the three ways people actually look for something: by document
  // number (title), by vendor/material (subtitle), by who raised it — plus gpo_no, which
  // answers "everything waiting on GPO 1741" and had no other way to be asked.
  const passQ = React.useCallback((r) => {
    const needle = norm(q.trim());
    if (!needle) return true;
    return [r.title, r.subtitle, r.raised_by, r.gpo_no].some(f => norm(f).includes(needle));
  }, [q]);

  const shown = React.useMemo(() => rows.filter(r => passCompany(r) && passType(r) && passQ(r)),
    [rows, passCompany, passType, passQ]);
  const hidden = rows.length - shown.length;

  // Facet counts exclude their OWN facet, so a count never reads as zero for something you
  // could still switch to. Standard faceting: the Deroi count is "Deroi under the current
  // type + search", not "Deroi under a company filter that already excludes it".
  const companyCounts = React.useMemo(() => {
    const base = rows.filter(r => passType(r) && passQ(r));
    return { RSR: base.filter(r => r.company === 'RSR').length, Deroi: base.filter(r => r.company === 'Deroi').length };
  }, [rows, passType, passQ]);
  const typeCounts = React.useMemo(() => {
    const base = rows.filter(r => passCompany(r) && passQ(r));
    const m = {};
    for (const r of base) m[r.type] = (m[r.type] || 0) + 1;
    // Only types PRESENT in this tab get a chip — a row of permanent zeroes is noise.
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [rows, passCompany, passQ]);

  // A filter change can hide the row that is mid-confirmation, which would leave `confirming`
  // pointing at something no longer on screen — armed, invisible, and re-appearing armed when
  // the filter is cleared. Disarm on any view change.
  React.useEffect(() => { setConfirming(null); setReason(''); }, [company, types, q, status]);

  // Identity of a row in the confirmation state. type+id, because id alone is not unique
  // across streams (a PO and a request can share neither table nor uuid space, but the key
  // is what the row list is already keyed by — keep them the same).
  const rowKey = (r) => `${r.type}-${r.id}`;
  // A held receipt needs two more answers than every other stream: WHICH reason, and at WHAT
  // figure the lot and cost should book. Kept beside `reason` rather than folded into it, so the
  // generic approve/reject path is untouched for the eight streams that do not need them.
  const [resolution, setResolution] = React.useState('reweighed');
  const [ruledQty, setRuledQty] = React.useState('');
  const closeConfirm = () => { setConfirming(null); setReason(''); setResolution('reweighed'); setRuledQty(''); };

  // CONFIRMATION LIVES IN THE ROW, NOT IN A BROWSER DIALOG (2026-08-18).
  //
  // This used to gate approve behind window.confirm and reject behind window.prompt. A
  // browser dialog is suppressible — Chrome's "prevent additional dialogs" checkbox sticks
  // for the life of the tab — and a suppressed confirm returns false, so act() returned
  // BEFORE the try block: no request, no error banner, nothing in the api log. A click that
  // silently did nothing was indistinguishable from one that worked, and Approve stayed dead
  // for the rest of the session. That is how DYC-2026-006 was believed approved while the PO
  // sat at draft with no po_approved row and no PATCH ever reaching the server.
  //
  // The confirmation is now an inline swap of the action cell — the same treatment as the
  // huddle owe picker (Huddle.js:705): the buttons are replaced in place by the control that
  // completes the decision, so an un-confirmed action is VISIBLE as a row still waiting.
  const act = async (row, decision, reasonText, extra) => {
    const a = ACT[row.type];
    if (!a) { setErr(`No action route for ${row.type}`); return; }
    // The reject reason rule is unchanged and still enforced by the endpoint; this only
    // stops a doomed round-trip. An empty reason cannot reach the server from here.
    const reasonTrimmed = (reasonText || '').trim();
    if (decision === 'reject' && !reasonTrimmed) { setErr('A reason is required to reject.'); return; }
    closeConfirm();
    setBusy(row.id); setErr(''); setNotice('');
    try {
      const res = await (decision === 'approve' ? a.approve(row.id, extra) : a.reject(row.id, reasonTrimmed));
      // Approving a payment request CREATES A PAYABLE. /requests/:id/decision returns its
      // invoice no and the request screen has always announced it — the hub discarded the
      // response, so an approver acting from here was never told a liability had been booked.
      const payable = res?.data?.payable_invoice_no;
      setNotice(payable
        ? `✅ ${row.title} approved — payable ${payable} created. It is now live on the Payables tile.`
        : `${decision === 'approve' ? '✅' : '❌'} ${row.title} ${decision === 'approve' ? 'approved' : 'rejected'}.`);
      load();
    } catch (e) {
      // A 403 here is the per-stream guard holding (raiser≠approver, wrong role) — surface it.
      setErr(`${row.title}: ${e.response?.data?.error || 'Action failed'}`);
    } finally { setBusy(''); }
  };

  const COMPANY = { RSR: { bg: '#EEEDF8', c: '#3B3A8F' }, Deroi: { bg: '#EAF7EE', c: '#166534' } };
  const th = { padding: '9px 10px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' };
  const td = { padding: '9px 10px', fontSize: 13, borderBottom: '1px solid #f5f5f5', verticalAlign: 'top' };
  const tabBtn = (id, label) => (
    <button key={id} onClick={() => { setParams({ status: id === 'pending' ? null : id }); setNotice(''); setErr(''); }} style={{
      padding: '8px 16px', border: 'none', borderBottom: status === id ? '3px solid #3B3A8F' : '3px solid transparent',
      background: 'transparent', color: status === id ? '#3B3A8F' : '#888', fontWeight: status === id ? 700 : 500,
      fontSize: 13, cursor: 'pointer' }}>{label}</button>
  );

  return (
    <div>
      <div style={{ marginBottom: 14, padding: '9px 13px', background: '#EEEDF8', borderRadius: 6, fontSize: 13, color: '#3B3A8F' }}>
        Everything awaiting approval, <strong>both companies (RSR + Deroi)</strong>, in one list. Each Approve/Reject goes to the item's own approval flow — the hub only shows &amp; routes.
      </div>
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #eee', marginBottom: 14 }}>
        {tabBtn('pending', `Pending (${status === 'pending' ? rows.length : '·'})`)}
        {tabBtn('approved', 'Approved')}
        {tabBtn('rejected', 'Rejected')}
      </div>

      {/* ── FILTER BAR ───────────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
        <div style={{ display: 'flex', gap: 3, background: '#F7F6F3', borderRadius: 6, padding: 3 }}>
          {[['', 'All'], ['RSR', `RSR (${companyCounts.RSR})`], ['Deroi', `Deroi (${companyCounts.Deroi})`]].map(([v, label]) => (
            <button key={v || 'all'} onClick={() => setParams({ co: v || null })} style={{
              padding: '5px 11px', border: 'none', borderRadius: 5, fontSize: 12, cursor: 'pointer',
              fontWeight: company === v ? 700 : 500,
              background: company === v ? '#3B3A8F' : 'transparent', color: company === v ? '#fff' : '#666' }}>{label}</button>
          ))}
        </div>
        <input value={qInput} onChange={e => setQInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Escape') { setQInput(''); setParams({ q: null }); } }}
          placeholder="Search no., vendor, GPO, raised by…"
          style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, minWidth: 230, flex: '0 1 300px' }} />
      </div>

      {/* Type chips — multi-select, because "POs and Payments" is a real way to look. */}
      {typeCounts.length > 1 && (
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 8 }}>
          {typeCounts.map(([t, n]) => {
            const on = types.includes(t);
            return (
              <button key={t} onClick={() => setParams({ ty: on ? types.filter(x => x !== t) : [...types, t] })} style={{
                padding: '4px 10px', borderRadius: 12, fontSize: 11, cursor: 'pointer', fontWeight: on ? 700 : 500,
                border: `1px solid ${on ? '#3B3A8F' : '#e2e2ea'}`,
                background: on ? '#EEEDF8' : '#fff', color: on ? '#3B3A8F' : '#777' }}>
                {TYPE_LABEL[t] || t} {n}
              </button>
            );
          })}
        </div>
      )}

      {/* COUNT LINE — total / shown / hidden, always all three, so a filter can never make
          the queue look shorter than it is. */}
      <div style={{ fontSize: 12, color: '#777', marginBottom: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span><strong style={{ color: '#3B3A8F' }}>{rows.length}</strong> {status}</span>
        <span style={{ color: '#ccc' }}>·</span>
        <span><strong style={{ color: '#333' }}>{shown.length}</strong> shown</span>
        <span style={{ color: '#ccc' }}>·</span>
        <span style={{ color: hidden > 0 ? '#854F0B' : '#bbb' }}>
          <strong>{hidden}</strong> hidden by filter
        </span>
        {filtered && (
          <button onClick={() => setParams({ co: null, ty: null, q: null })} style={{
            padding: '2px 9px', border: '1px solid #ddd', background: '#fff', borderRadius: 10,
            fontSize: 11, color: '#3B3A8F', cursor: 'pointer', fontWeight: 600 }}>Clear filters</button>
        )}
      </div>
      {status === 'pending' && <ExcessCountersign />}
      {err && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{err}</div>}
      {notice && <div style={{ background: '#EAF7EE', color: '#166534', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13, fontWeight: 600 }}>{notice}</div>}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>{['Company', 'Type', 'Item', 'Details', 'Amount', 'Level', 'Raised by', 'When', status === 'pending' ? 'Action' : ''].map((h, i) => <th key={i} style={th}>{h}</th>)}</tr></thead>
        <tbody>
          {/* Two DIFFERENT empty states. "Nothing pending" and "your filter matched nothing"
              are opposite facts, and showing the first under an active filter would read as
              an empty queue — the exact misreading this filter exists to prevent. */}
          {rows.length === 0 && <tr><td colSpan={9} style={{ ...td, textAlign: 'center', color: '#aaa', padding: 24 }}>Nothing {status}.</td></tr>}
          {rows.length > 0 && shown.length === 0 && (
            <tr><td colSpan={9} style={{ ...td, textAlign: 'center', color: '#854F0B', padding: 24 }}>
              None of the {rows.length} {status} items match this filter.{' '}
              <button onClick={() => setParams({ co: null, ty: null, q: null })} style={{
                border: 'none', background: 'none', color: '#3B3A8F', cursor: 'pointer', fontWeight: 700, fontSize: 13, textDecoration: 'underline' }}>Clear filters</button>
            </td></tr>
          )}
          {shown.map(r => {
            const co = COMPANY[r.company] || COMPANY.RSR;
            return (
              <tr key={`${r.type}-${r.id}`}>
                <td style={td}><span style={{ padding: '2px 9px', borderRadius: 8, fontSize: 11, fontWeight: 700, background: co.bg, color: co.c }}>{r.company}</span></td>
                <td style={td}><span style={{ fontSize: 11, color: '#666' }}>{TYPE_LABEL[r.type] || r.type}</span></td>
                {/* rev chip — same styling as the PO list (Procurement.js:1630), so an amended
                    item reads the same wherever it is seen. */}
                <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700, color: '#3B3A8F' }}>
                  {r.title}
                  {r.revision > 0 && (
                    <span title="This item has been amended and returned for approval"
                      style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, background: '#EEEDF8', color: '#3B3A8F', padding: '1px 6px', borderRadius: 6 }}>
                      rev {r.revision}
                    </span>
                  )}
                </td>
                <td style={{ ...td, maxWidth: 260, color: '#555' }}>
                  {r.subtitle || '—'}
                  {status === 'pending' && r.type === 'purchase_order' && (r.price_ctx_lines || []).length > 0 &&
                    <PriceContextCell lines={r.price_ctx_lines} detailLink={linkFor(r)} gpoNo={r.gpo_no} />}
                  {r.type === 'indent_general' && (r.lines || []).length > 0 &&
                    <IndentLinesCell lines={r.lines} detailLink={linkFor(r)} />}
                  {/* A payment request is approved inline here, so nothing else on the row linked
                      to it — the approver had no way to open the record they were deciding. */}
                  {r.type === 'payment' && r.detail_link && (
                    <div style={{ marginTop: 4, fontSize: 11 }}>
                      <a href={linkFor(r)} style={{ color: '#3B3A8F', fontWeight: 600, textDecoration: 'none' }}>detail →</a>
                    </div>
                  )}
                </td>
                <td style={td}>{r.amount != null ? `₹${num(r.amount).toLocaleString('en-IN')}` : '—'}</td>
                {/* WHERE IT WAITS AND ON WHOM (mig 379): "Waiting for Rajan — recommended by Nainja", not a bare level. */}
                <td style={{ ...td, color: r.waiting_on && /^Waiting for Rajan|final approver/.test(r.waiting_on) ? '#7A5A00' : '#888', maxWidth: 220 }}>{r.waiting_on || r.current_level || '—'}</td>
                <td style={td}>{r.raised_by}</td>
                {/* WHEN — two dates, not one. The list is sorted on pending_since (when the item
                    entered this queue), so showing only raised_at made an amended PO read as
                    weeks old and look like it had been skipped. Where they differ, both show. */}
                <td style={{ ...td, color: '#888', whiteSpace: 'nowrap' }}>
                  <div>{(r.raised_at || '').slice(0, 10)}</div>
                  {r.reopened_at && (r.reopened_at || '').slice(0, 10) !== (r.raised_at || '').slice(0, 10) && (
                    <div style={{ color: '#B45309', fontWeight: 600, fontSize: 11, marginTop: 2 }}
                      title={`Re-entered the approval queue on ${(r.reopened_at || '').slice(0, 10)} — it is sorted by this date, not the raised date`}>
                      ↻ reopened {(r.reopened_at || '').slice(0, 10)}
                    </div>
                  )}
                </td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  {status === 'pending' && (r.action === 'inline' && ACT[r.type] ? (
                    confirming && confirming.key === rowKey(r) ? (
                      /* ARMED — the buttons are gone and the row shows what it is about to do.
                         Escape backs out; Enter commits once the reject reason is non-empty. */
                      <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap', whiteSpace: 'normal' }}
                        onKeyDown={e => { if (e.key === 'Escape') closeConfirm(); }}>
                        {confirming.decision === 'approve' && r.type === 'jobwork_hold' ? (
                          /* THE HOLD'S OWN CONTROL. "moisture" is offered and the endpoint refuses
                             it above the issued figure (ruled 25 Sep) — knitting is not paid on
                             water, and such a claim is a reason to reweigh. The refusal names that,
                             so the option stays visible rather than being hidden and unexplained. */
                          <>
                            <select value={resolution} onChange={e => setResolution(e.target.value)}
                              style={{ padding: '5px 8px', border: '1px solid #ddd', borderRadius: 5, fontSize: 12 }}>
                              <option value="reweighed">Reweighed — the first weight was wrong</option>
                              <option value="moisture">Moisture</option>
                              <option value="knitter_yarn">Knitter's own yarn</option>
                            </select>
                            <input value={ruledQty} onChange={e => setRuledQty(e.target.value)} type="number" step="0.001"
                              placeholder="Book at (kg)" title="The figure the lot and the cost book at. Leave blank to book what was weighed."
                              style={{ padding: '5px 8px', border: '1px solid #ddd', borderRadius: 5, fontSize: 12, width: 110 }} />
                            <input value={reason} onChange={e => setReason(e.target.value)}
                              placeholder="Reason (required)"
                              style={{ padding: '5px 8px', border: '1px solid #E0B4B4', borderRadius: 5, fontSize: 12, width: 170 }} />
                          </>
                        ) : confirming.decision === 'reject' || r.type === 'jobwork_excess' ? (
                          <input autoFocus value={reason} onChange={e => setReason(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter' && reason.trim()) act(r, confirming.decision, reason, confirming.decision === 'approve' ? { reason } : undefined); }}
                            placeholder="Reason (required)"
                            style={{ padding: '5px 8px', border: '1px solid #E0B4B4', borderRadius: 5, fontSize: 12, width: 170 }} />
                        ) : (
                          <span style={{ fontSize: 11, color: '#555' }}>
                            Approve {r.company}{r.amount != null ? ` · ₹${num(r.amount).toLocaleString('en-IN')}` : ''}?
                          </span>
                        )}
                        <button autoFocus={confirming.decision === 'approve'}
                          /* PRESSABLE (2026-08-26): act() already answers "A reason is required to reject." and
                             this expression made that answer unreachable, across eight approval streams. */
                          disabled={busy === r.id}
                          onClick={() => act(r, confirming.decision, reason,
                            r.type === 'jobwork_hold' && confirming.decision === 'approve'
                              ? { resolution, reason, ...(ruledQty === '' ? {} : { ruled_qty: Number(ruledQty) }) }
                              : r.type === 'jobwork_excess' && confirming.decision === 'approve' ? { reason } : undefined)}
                          style={{ padding: '5px 12px', border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                            background: confirming.decision === 'approve' ? '#166534' : '#A32D2D', color: '#fff',
                            opacity: ((confirming.decision === 'reject' || r.type === 'jobwork_hold' || r.type === 'jobwork_excess') && !reason.trim()) ? 0.45 : 1 }}>
                          {busy === r.id ? '…' : confirming.decision === 'approve' ? (r.current_level === 'final_approver' ? (r.i_am_final_approver ? '✓ Yes, approve (final)' : '✓ Yes, recommend') : '✓ Yes, approve') : '✕ Yes, reject'}
                        </button>
                        <button onClick={closeConfirm}
                          style={{ padding: '5px 10px', background: 'transparent', border: '1px solid #ddd', borderRadius: 5, color: '#666', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                      </div>
                    ) : (
                    <>
                      <button disabled={busy === r.id} onClick={() => { setReason(''); setConfirming({ key: rowKey(r), decision: 'approve' }); }} style={{ padding: '5px 12px', background: '#EAF7EE', border: 'none', borderRadius: 5, color: '#166534', cursor: 'pointer', fontSize: 12, fontWeight: 600, marginRight: 5 }}>{busy === r.id ? '…' : r.current_level === 'final_approver' ? (r.i_am_final_approver ? '✓ Approve (final)' : '✓ Recommend') : '✓ Approve'}</button>
                      <button disabled={busy === r.id} onClick={() => { setReason(''); setConfirming({ key: rowKey(r), decision: 'reject' }); }} style={{ padding: '5px 12px', background: '#FCEBEB', border: 'none', borderRadius: 5, color: '#A32D2D', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>✕ Reject</button>
                    </>
                    )
                  ) : (
                    <a href={linkFor(r)} style={{ padding: '5px 12px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 5, color: '#3B3A8F', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>Review →</a>
                  ))}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
